/**
 * Cross-tree refresh signal for the Initial Documents right-rail card.
 *
 * The card lives in the dashboard right-rail / onboarding sidebar, but other
 * parts of the app (e.g. the "Auto Fill Form" CV upload on the assessment
 * form) can also mutate the user's USER-scoped documents on the backend.
 * When that happens the card needs to refetch so the row state (Provided /
 * Missing, document name, etc.) reflects the new server state.
 *
 * Rather than coupling those features to the card via props or redux, we
 * expose a window-level CustomEvent that the card subscribes to and any
 * feature can emit from anywhere in the tree (or from a callback nested
 * inside an unrelated component).
 */
export const INITIAL_DOCUMENTS_REFRESH_EVENT = 'aliro:initial-documents:refresh';

export type InitialDocumentsRefreshDetail = {
    /**
     * Optional candidate / user id scope. When provided, the card should
     * only refetch if its own `userId` matches — this prevents an event
     * intended for one candidate from forcing a refetch of an Initial
     * Documents card mounted for a different candidate elsewhere in the
     * tree (e.g. while debugging or in future multi-pane layouts).
     *
     * When omitted, every mounted card refetches.
     */
    userId?: number | string | null;
};

/**
 * Dispatch a refresh signal. Safe to call from anywhere — no-ops outside
 * a browser environment (SSR, jest without jsdom, etc.).
 */
export function emitInitialDocumentsRefresh(detail: InitialDocumentsRefreshDetail = {}): void {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(
        new CustomEvent<InitialDocumentsRefreshDetail>(INITIAL_DOCUMENTS_REFRESH_EVENT, {
            detail,
        })
    );
}
